const MAX_DECODED_MEDIA_PIXELS = 16_000_000
const MAX_DECODED_MEDIA_ENTRIES = 64
const FALLBACK_MEDIA_PIXELS = 1_000_000

type DecodedMediaEntry = {
    image: HTMLImageElement
    pixels: number
}

type MediaCandidate = {
    element?: HTMLImageElement
    height?: number
    url: string
    width?: number
}

const decodedMedia = new Map<string, DecodedMediaEntry>()
const pendingMedia = new Map<string, Promise<boolean>>()
let decodedMediaPixels = 0

function normalizedPixels(width?: number, height?: number) {
    const measured = Number(width || 0) * Number(height || 0)
    return Number.isFinite(measured) && measured > 0 ? measured : FALLBACK_MEDIA_PIXELS
}

function touchDecodedMedia(url: string) {
    const entry = decodedMedia.get(url)
    if (!entry) return false

    decodedMedia.delete(url)
    decodedMedia.set(url, entry)
    return true
}

function enforcePixelBudget() {
    while (
        (decodedMediaPixels > MAX_DECODED_MEDIA_PIXELS ||
            decodedMedia.size > MAX_DECODED_MEDIA_ENTRIES) &&
        decodedMedia.size > 0
    ) {
        const oldestKey = decodedMedia.keys().next().value as string | undefined
        if (!oldestKey) break
        const oldest = decodedMedia.get(oldestKey)
        decodedMedia.delete(oldestKey)
        decodedMediaPixels -= oldest?.pixels || 0
    }
}

function potentiallyAnimatedMedia(url: string) {
    const normalizedUrl = url.toLowerCase()
    const path = normalizedUrl.split(/[?#]/, 1)[0]
    return path.endsWith('.gif') || normalizedUrl.startsWith('data:image/gif')
}

function rememberDecodedMedia(url: string, image: HTMLImageElement, pixels: number) {
    const previous = decodedMedia.get(url)
    if (previous) {
        decodedMediaPixels -= previous.pixels
        decodedMedia.delete(url)
    }

    // A single resource larger than the full budget is useful for the immediate transition,
    // but retaining it would evict every neighboring asset and still exceed the bound.
    if (pixels > MAX_DECODED_MEDIA_PIXELS) return

    decodedMedia.set(url, { image, pixels })
    decodedMediaPixels += pixels
    enforcePixelBudget()
}

function decodeCandidate(candidate: MediaCandidate) {
    const { element, height, url, width } = candidate
    if (!url || potentiallyAnimatedMedia(url) || typeof Image === 'undefined') {
        return Promise.resolve(false)
    }
    if (touchDecodedMedia(url)) return Promise.resolve(true)

    const pending = pendingMedia.get(url)
    if (pending) return pending

    if (element) {
        element.loading = 'eager'
        element.setAttribute('fetchpriority', 'high')
    }

    const decode = (async () => {
        try {
            // Decode the actual destination node as well as a fiber-free keeper. The keeper
            // holds the browser's decoded resource after the React media subtree is parked.
            const keeper = new Image()
            keeper.decoding = 'async'
            keeper.src = url

            const decodes: Promise<unknown>[] = []
            if (typeof element?.decode === 'function') decodes.push(element.decode())
            if (typeof keeper.decode === 'function') decodes.push(keeper.decode())
            const results = await Promise.allSettled(decodes)
            if (results.length > 0 && results.every(result => result.status === 'rejected')) {
                return false
            }

            const pixels = normalizedPixels(
                keeper.naturalWidth || element?.naturalWidth || width,
                keeper.naturalHeight || element?.naturalHeight || height,
            )
            rememberDecodedMedia(url, keeper, pixels)
            return true
        } catch {
            return false
        } finally {
            pendingMedia.delete(url)
        }
    })()

    pendingMedia.set(url, decode)
    return decode
}

function collectPanelMedia(scope: ParentNode, loadedOnly = false): MediaCandidate[] {
    const candidates = new Map<string, MediaCandidate>()
    const scopeRect = scope instanceof Element ? scope.getBoundingClientRect() : null
    const isNearViewport = (node: Element) => {
        if (!scopeRect) return true
        const rect = node.getBoundingClientRect()
        const viewportMargin = scopeRect.height
        return (
            rect.bottom >= scopeRect.top - viewportMargin &&
            rect.top <= scopeRect.bottom + viewportMargin
        )
    }

    scope.querySelectorAll('img').forEach(node => {
        const image = node as HTMLImageElement
        if (!isNearViewport(image)) return
        if (loadedOnly && (!image.complete || image.naturalWidth === 0)) return
        const url = image.currentSrc || image.src
        if (!url) return
        candidates.set(url, {
            element: image,
            height: image.naturalHeight || image.height,
            url,
            width: image.naturalWidth || image.width,
        })
    })

    scope.querySelectorAll('video[poster]').forEach(node => {
        const video = node as HTMLVideoElement
        if (!isNearViewport(video)) return
        const url = video.poster
        if (!url || candidates.has(url)) return
        candidates.set(url, {
            height: video.videoHeight || video.height,
            url,
            width: video.videoWidth || video.width,
        })
    })

    return [...candidates.values()]
}

async function warmPanelMedia(scope: ParentNode | null, timeoutMs: number, loadedOnly: boolean) {
    if (!scope) return { decoded: 0, timedOut: false, total: 0 }

    const candidates = collectPanelMedia(scope, loadedOnly)
    if (candidates.length === 0) return { decoded: 0, timedOut: false, total: 0 }

    let timedOut = false
    let timeoutId = 0
    const timeout = new Promise<null>(resolve => {
        timeoutId = window.setTimeout(() => {
            timedOut = true
            resolve(null)
        }, Math.max(0, timeoutMs))
    })
    const results = await Promise.race([Promise.all(candidates.map(decodeCandidate)), timeout])
    window.clearTimeout(timeoutId)

    return {
        decoded: Array.isArray(results) ? results.filter(Boolean).length : 0,
        timedOut,
        total: candidates.length,
    }
}

export function warmPanelMediaForTransition(scope: ParentNode | null, timeoutMs = 48) {
    return warmPanelMedia(scope, timeoutMs, false)
}

export function retainDecodedPanelMedia(scope: ParentNode | null) {
    // Parking should preserve media already paid for, not start new offscreen image fetches.
    void warmPanelMedia(scope, 0, true)
}

export function getDecodedPanelMediaCacheStats() {
    return {
        entries: decodedMedia.size,
        maxEntries: MAX_DECODED_MEDIA_ENTRIES,
        maxPixels: MAX_DECODED_MEDIA_PIXELS,
        pixels: decodedMediaPixels,
    }
}
